import { PcbNodeTypes, } from "@buildwithflux/models";
import { clampBetween } from "@buildwithflux/shared";
import { normalizePcbLayoutNodeType } from "../helpers/normalizePcbLayoutNodeType";
/** Only pads, traces vias and elements have the keepOut rule */
export function canNodeHaveKeepout(node) {
    return (node.type === PcbNodeTypes.pad ||
        node.type === PcbNodeTypes.via ||
        node.type === PcbNodeTypes.routeSegment ||
        node.type === PcbNodeTypes.element ||
        node.type === PcbNodeTypes.net ||
        node.type === PcbNodeTypes.zone ||
        node.type === PcbNodeTypes.smartVia ||
        node.type === PcbNodeTypes.polygon);
}
const pcbNodeTypesValues = Object.values(PcbNodeTypes);
/**
 * Matches a keepout selector to a node, uses hardcoded logic to go faster
 * and allows matching with type only as well.
 */
function keepOutSelectorMatch(selector, uid, nodeType, nodeNetUid, pcbLayoutSelectorMatching) {
    // HACK: To increase performance, we hardcode some selector special cases
    if (selector === "*")
        return 0;
    const normalizedName = normalizePcbLayoutNodeType(selector);
    if (nodeType === normalizedName)
        return 1;
    else if (pcbNodeTypesValues.includes(normalizedName))
        return undefined;
    // We allow not passing uid, since we want to allow also type matching for fills
    if (pcbLayoutSelectorMatching && uid) {
        return (pcbLayoutSelectorMatching.match(selector, uid) ||
            (nodeNetUid ? pcbLayoutSelectorMatching.match(selector, nodeNetUid) : undefined));
    }
    return undefined;
}
/**
 * Given two nodes (with uid and type) and two sets of keepOut rules, find the
 * most best keepOut value between the two. It explores both the keepOut rules
 * from node A to B and B to A.
 * It's also possible to not pass an UID, rules and pcbLayoutSelectorMatching,
 * if you want to match with type only. This is useful with nodes that cannot
 * have keepOut rules, such as fills. In that case you are only interested in
 * the keepOut from the pad to the fill, not the other way around.
 *
 * NOTE: Clamping still has to be performed afterwards, so call this function
 * multiple times passing keepOut, maxKeepOut and minKeepOut.
 */
function findBestKeepOutRule(uidA, uidB, typeA, typeB, rulesA, rulesB, nodeUidB, nodeUidA, pcbLayoutSelectorMatching) {
    // We need to evaluate possible keepouts from both A to B and B to A
    const matchesA = rulesA?.map((ko) => ({
        value: ko.value,
        specificity: keepOutSelectorMatch(ko.selector, uidB, typeB, nodeUidA, pcbLayoutSelectorMatching),
    }));
    const matchesB = rulesB?.map((ko) => ({
        value: ko.value,
        specificity: keepOutSelectorMatch(ko.selector, uidA, typeA, nodeUidB, pcbLayoutSelectorMatching),
    }));
    const matches = [...(matchesA ?? []), ...(matchesB ?? [])]
        .filter((m) => m.specificity !== undefined)
        .sort((a, b) => (b.specificity ?? 0) - (a.specificity ?? 0));
    if (matches.length === 0)
        return false;
    // The keepouts with the bigger specificity wins, but when multiple have
    // the same specificity, we take the biggest one to be safe
    const maxSpecificity = matches[0]?.specificity;
    const matchesWithMaxSpecificity = matches.filter((m) => m.specificity === maxSpecificity);
    const minDist = Math.max(...matchesWithMaxSpecificity.map((m) => m.value));
    return minDist;
}
/**
 * Finds the keepout value from nodeA to nodes of type typeB
 * @deprecated Don't use this! Keepouts should be more precise than node vs type, keep everything between two single nodes whenever possible.
 * */
export function calcKeepoutForNodeAndType(nodeA, nodeABakedRules, typeB) {
    // Resolve overloads
    const bakedRules = "bakedRules" in nodeA ? nodeA.bakedRules : nodeABakedRules;
    const keepOut = findBestKeepOutRule(nodeA.uid, undefined, nodeA.type, typeB, bakedRules.keepOut ?? [], undefined, undefined, undefined, undefined);
    const minKeepOut = findBestKeepOutRule(nodeA.uid, undefined, nodeA.type, typeB, bakedRules.minKeepOut ?? [], undefined, undefined, undefined, undefined);
    const maxKeepOut = findBestKeepOutRule(nodeA.uid, undefined, nodeA.type, typeB, bakedRules.maxKeepOut ?? [], undefined, undefined, undefined, undefined);
    const value = keepOut || 0;
    const minValue = minKeepOut || 0;
    const maxValue = maxKeepOut === false ? Infinity : maxKeepOut;
    return clampBetween(value, minValue, maxValue);
}
/**
 * Finds the biggest keepout value that a node might have
 * @deprecated It's not always possible to compute the biggest keepout for a node, so this value could be wrong.
 * This is right now only used for the auto positioner, which is not mission critical anyway.
 */
export function calcMaxKeepoutForNode(nodeA) {
    const keepOut = Math.max(...(nodeA.bakedRules?.keepOut?.map((ko) => ko.value) ?? []));
    const minKeepOut = Math.max(...(nodeA.bakedRules?.minKeepOut?.map((ko) => ko.value) ?? []));
    const clampedKeepOut = Math.max(keepOut ?? 0, minKeepOut ?? 0);
    return clampedKeepOut;
}
function getNetForNode(node) {
    return "effectiveNetId" in node.bakedRules ? node.bakedRules.effectiveNetId : undefined;
}
/**
 * Finds the keepout value from nodeA to nodeB
 * This is the only function that can get a reliable value
 */
export function calcKeepoutForNodes(nodeA, nodeB, pcbLayoutSelectorMatching) {
    // Net membership works the other way around as well:
    // Applying a keepout rule to a net will propagate to all the member as well
    // ...but also a keepout value targeting a net will match all the members of that net
    const netUidA = getNetForNode(nodeA);
    const netUidB = getNetForNode(nodeB);
    const keepOut = findBestKeepOutRule(nodeA.uid, nodeB.uid, nodeA.type, nodeB.type, nodeA.bakedRules.keepOut ?? [], nodeB.bakedRules.keepOut ?? [], netUidA, netUidB, pcbLayoutSelectorMatching);
    const minKeepOut = findBestKeepOutRule(nodeA.uid, nodeB.uid, nodeA.type, nodeB.type, nodeA.bakedRules.minKeepOut ?? [], nodeB.bakedRules.minKeepOut ?? [], netUidA, netUidB, pcbLayoutSelectorMatching);
    const maxKeepOut = findBestKeepOutRule(nodeA.uid, nodeB.uid, nodeA.type, nodeB.type, nodeA.bakedRules.maxKeepOut ?? [], nodeB.bakedRules.maxKeepOut ?? [], netUidA, netUidB, pcbLayoutSelectorMatching);
    const value = keepOut || 0;
    const minValue = minKeepOut || 0;
    const maxValue = maxKeepOut === false ? Infinity : maxKeepOut;
    return clampBetween(value, minValue, maxValue);
}
//# sourceMappingURL=keepOutHelpers.js.map